/**
 * The zone course of a trip (1.87.0) -- the one place in the browser that
 * turns a moment into a wall clock for something without a measured offset
 * of its own. Its twin on the server is `TripZones.php`; both follow
 * `docs/zeit.md`, and there is to be no third.
 *
 * **Two rules.** An offset measured in its moment stays on its row (message,
 * point, weather card) and is read as it is -- safe across a daylight saving
 * change, it was right for that moment. For every other moment the offset is
 * computed from a zone **name** for exactly that moment, never "the offset
 * of now" carried over. The names come from the trip's course: the first
 * entry has no start and covers everything before the second.
 *
 * `Intl` knows the zones and their changes on every device; nothing is
 * shipped for it.
 */

/** One entry of the course, as the server sends it with the trip. */
export interface Zoneneintrag {
  id: number
  /** UTC, ISO; null for the first entry, which covers everything before the next. */
  valid_from: string | null
  /** A zone name such as `Europe/Berlin`. */
  tz_name: string
}

export type Verlauf = readonly Zoneneintrag[]

const MINUTE = 60_000

/** Whether a clock is a course (and not a measured number). */
export function istVerlauf(x: unknown): x is Verlauf {
  return Array.isArray(x)
}

/** The zone name valid at a moment -- null for an empty course. */
export function zoneBei(verlauf: Verlauf, iso: string | number): string | null {
  const t = typeof iso === 'number' ? iso : new Date(iso).getTime()
  if (Number.isNaN(t)) return null
  let name: string | null = null
  // Ordered as the server sends it: the one without start first, then by start.
  for (const e of verlauf) {
    if (e.valid_from !== null && new Date(e.valid_from).getTime() > t) break
    name = e.tz_name
  }
  return name
}

/**
 * The offset in minutes of a zone at a moment, daylight saving included.
 * Read off `Intl`: the moment formatted in that zone, read back as if it
 * were UTC, minus the moment itself.
 */
const formate = new Map<string, Intl.DateTimeFormat>()
export function versatzIn(name: string, iso: string | number): number {
  const t = typeof iso === 'number' ? iso : new Date(iso).getTime()
  let f = formate.get(name)
  if (f === undefined) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: name,
      hourCycle: 'h23',
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', second: 'numeric',
    })
    formate.set(name, f)
  }
  const teile: Record<string, number> = {}
  for (const p of f.formatToParts(new Date(t))) {
    if (p.type !== 'literal') teile[p.type] = Number(p.value)
  }
  const n = (k: string): number => teile[k] ?? 0
  const alsUtc = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'), n('second'))
  // Whole minutes: the seconds of the moment itself are not part of the offset.
  return Math.round((alsUtc - Math.floor(t / 1000) * 1000) / MINUTE)
}

/** The offset of the course at a moment -- null for an empty course. */
export function versatzBei(verlauf: Verlauf, iso: string | number): number | null {
  const name = zoneBei(verlauf, iso)
  return name === null ? null : versatzIn(name, iso)
}

/**
 * A wall clock (`YYYY-MM-DDTHH:mm` or with seconds, as a camera or a form
 * gives it) to UTC, in the zone valid at that moment.
 *
 * The same steps as `TripZones::wallToUtc()`, and the same answers inside a
 * change: a wall clock in the skipped hour (02:30 on a spring night) moves
 * forward, one in the doubled hour of autumn takes the second -- what PHP
 * does, measured, so that the server's recomputing and the browser's upload
 * agree.
 */
export function wanduhrZuUtc(verlauf: Verlauf, wanduhr: string): string | undefined {
  const roh = wanduhr.trim()
  if (roh === '') return undefined
  const alsUtc = new Date((roh.length === 16 ? roh + ':00' : roh.slice(0, 19)) + 'Z').getTime()
  if (Number.isNaN(alsUtc)) return undefined
  const name = zoneBei(verlauf, alsUtc)
  if (name === null) return undefined
  const v1 = versatzIn(name, alsUtc)
  const u1 = alsUtc - v1 * MINUTE
  const zweiterName = zoneBei(verlauf, u1) ?? name
  const v2 = versatzIn(zweiterName, u1)
  if (v2 === v1 && zweiterName === name) return new Date(u1).toISOString()
  // The guess fell into another offset (a change, or another entry): once
  // more with the offset found there -- in a gap that is the moment after it.
  return new Date(alsUtc - v2 * MINUTE).toISOString()
}

/** One change of offset inside a zone: the moment (UTC, ms) and the offset from then on. */
export interface Umstellung {
  ab: number
  versatz: number
}

const TAG = 24 * 60 * MINUTE

/**
 * The changes of offset of a zone between two moments (1.89.0) -- daylight
 * saving on and off, and any other change the zone database knows.
 *
 * **Shown, never stored.** From use: "dann ist die Darstellung in der
 * Zeitzonentabelle tatsächlich irreführend ... eine Reise dauert ein Jahr.
 * dann gibt es da, wenn man sich nur in Deutschland bewegt, dennoch 2
 * Zeitumstellungen." The course keeps names; the offset of a moment comes
 * from the name and the moment (`versatzIn`). A change is a consequence of
 * the name, not a decision of the author -- as an entry it could be moved or
 * removed and then be wrong. So the list derives it, from the same `Intl` the
 * app computes with.
 *
 * Found day by day and then to the minute: no zone changes twice within a
 * day, and a year is 366 questions to a cached formatter.
 */
export function umstellungenIn(name: string, von: number, bis: number): Umstellung[] {
  const ergebnis: Umstellung[] = []
  if (!(bis > von)) return ergebnis
  let vorher = von
  let alt = versatzIn(name, vorher)
  for (let t = Math.min(von + TAG, bis); ; t = Math.min(t + TAG, bis)) {
    const neu = versatzIn(name, t)
    if (neu !== alt) {
      // Halve the day down to the minute in which the offset turns.
      let links = vorher
      let rechts = t
      while (rechts - links > MINUTE) {
        const mitte = links + Math.floor((rechts - links) / 2 / MINUTE) * MINUTE
        if (versatzIn(name, mitte) === alt) links = mitte
        else rechts = mitte
      }
      ergebnis.push({ ab: rechts, versatz: neu })
      alt = neu
    }
    if (t >= bis) break
    vorher = t
  }
  return ergebnis
}

/**
 * Whether an offset of a zone is its summer time -- `null` for a zone
 * without any (Tokyo, UTC): there the word would say nothing.
 *
 * Asked against the year of the moment: the offsets in January and in July.
 * The larger of the two is summer time, in the north as in the south (Sydney
 * has it in January), so no hemisphere needs to be known.
 */
export function istSommerzeit(name: string, iso: string | number): boolean | null {
  const t = typeof iso === 'number' ? iso : new Date(iso).getTime()
  const jahr = new Date(t).getUTCFullYear()
  const januar = versatzIn(name, Date.UTC(jahr, 0, 1))
  const juli = versatzIn(name, Date.UTC(jahr, 6, 1))
  if (januar === juli) return null
  return versatzIn(name, t) === Math.max(januar, juli)
}

/** The local day (`YYYY-MM-DD`) of a moment in the course. */
export function tagIm(verlauf: Verlauf, iso: string): string {
  const v = versatzBei(verlauf, iso)
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return ''
  return new Date(t + (v ?? -new Date(t).getTimezoneOffset()) * MINUTE).toISOString().slice(0, 10)
}

/**
 * The UTC window of a local day: local midnight to the next local midnight,
 * 23 or 25 hours on the night of a change.
 */
export function tagFenster(verlauf: Verlauf, tag: string): [number, number] | null {
  const von = wanduhrZuUtc(verlauf, `${tag}T00:00`)
  const naechster = new Date(`${tag}T12:00:00Z`)
  if (von === undefined || Number.isNaN(naechster.getTime())) return null
  naechster.setUTCDate(naechster.getUTCDate() + 1)
  const bis = wanduhrZuUtc(verlauf, `${naechster.toISOString().slice(0, 10)}T00:00`)
  if (bis === undefined) return null
  return [new Date(von).getTime(), new Date(bis).getTime()]
}

/** The zone the device is in, as a name -- the first entry of a new trip, the banner's question. */
export function zoneDesGeraets(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/**
 * Whether two names mean the same zone (1.87.0). Chromium reports
 * `Asia/Calcutta`, the server stores `Asia/Kolkata` when its timezonedb
 * knows only that one (`TripZones::canonical()`); compared as strings, the
 * suggestion would stand forever. `Intl` resolves both to its own name.
 */
export function gleicheZone(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return a === b
  if (a === b) return true
  try {
    const name = (z: string) => new Intl.DateTimeFormat('en-US', { timeZone: z }).resolvedOptions().timeZone
    return name(a) === name(b)
  } catch {
    return false
  }
}

/**
 * Every zone the browser knows, for the list on the trip's page. Older
 * Safari (before 15.4) lacks `supportedValuesOf`; then the list holds the
 * device's own zone and the ones already in the course, which is enough to
 * keep or remove an entry and to add the zone one is in.
 */
export function alleZonen(dazu: readonly string[] = []): string[] {
  let liste: string[] = []
  try {
    const f = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf
    if (typeof f === 'function') liste = f('timeZone')
  } catch {
    liste = []
  }
  const alle = new Set([...liste, zoneDesGeraets(), ...dazu, 'UTC'])
  return [...alle].sort((a, b) => a.localeCompare(b))
}

/**
 * The city to write after a time of a day card (1.100.0) -- when the time
 * lies in another zone of the course than the card's start, else null.
 *
 * From use, with a screenshot: a flight Frankfurt -- New York, and the car
 * after it "18:38–19:11 · 109 km · 6:34 Std." Both numbers were right: every
 * time is written in the zone the course has at that moment, and the course
 * changed to New York between the two. Nothing said so. Decided: the course
 * stays the one source, and a time says its zone where it is another than
 * the start's.
 *
 * **Twice, on purpose and alike**: `DayCardView::zoneMark()` writes the
 * same for PDF and EPUB; `zonen.ts` in the Prüfstand and `DayCardTest` hold
 * the same cases.
 */
export function zoneMarkAt(clock: unknown, start: string | null, iso: string): string | null {
  if (!istVerlauf(clock) || start === null) return null
  const here = zoneBei(clock, iso)
  if (here === null || gleicheZone(here, zoneBei(clock, start))) return null
  return stadtVon(here)
}

/**
 * The two marks of a section's "from–to": a section that lies wholly in
 * the other zone says it once, after its end.
 */
export function sectionZoneMarks(clock: unknown, start: string | null, from: string, to: string): [string | null, string | null] {
  const after = zoneMarkAt(clock, start, to)
  const before = zoneMarkAt(clock, start, from)
  const whole = istVerlauf(clock) && gleicheZone(zoneBei(clock, from), zoneBei(clock, to))
  return [whole ? null : before, after]
}

/** A change of the course that belongs at a landing, and that landing. */
export interface LandingChange {
  entry: Zoneneintrag
  /** UTC, ISO: the end of the flight section. */
  landing: string
  /** Whether the change comes too early (before the take-off) and not too late. */
  early: boolean
}

const DAY_MS = 24 * 3600_000

/**
 * A change of the course that should be at a landing (1.100.0) -- or null.
 *
 * From use: the course said New York from 15:00 there, the flight had landed
 * at 11:12, and the day card wrote the landing and the car after it in
 * Berlin time. Decided: the course stays the one source, and the sheet
 * "Welcher Tag?" proposes to move the change to the landing. A change during
 * the flight is right as it is: nobody reads a clock in the air.
 *
 * **Too late**: the next change after the take-off comes after the landing,
 * within a day, to another zone than the one at the take-off.
 *
 * **Too early** (from use: "das passiert nicht oft bei einer Reise, aber
 * sollte dennoch richtig sein"): the change before the take-off came within
 * a day of it, and its zone does not fit the place of the take-off but fits
 * the landing -- New York already at Frankfurt. The same change can also be
 * yesterday's landing, and right: then its zone fits the take-off, and
 * nothing is proposed. Without the place of the take-off nothing is.
 *
 * "Fits" is the longitude: four minutes a degree, give or take two and a
 * half hours (New York at -73.8 is -295 against -240, Berlin at 8.6 is 34
 * against 120).
 */
export function landingChange(
  clock: unknown,
  sections: readonly { vehicle: string; from: string; to: string; points?: readonly (readonly number[])[] }[],
): LandingChange | null {
  if (!istVerlauf(clock)) return null
  const fits = (name: string, at: number, lon: number | undefined) =>
    lon === undefined || Math.abs(versatzIn(name, at) - lon * 4) <= 150
  const starts = clock
    .filter((e) => e.valid_from !== null)
    .map((e) => ({ e, at: Date.parse(e.valid_from!) }))
  for (const section of sections) {
    if (section.vehicle !== 'plane') continue
    const takeOff = Date.parse(section.from)
    const landing = Date.parse(section.to)
    const takeOffLon = section.points?.[0]?.[1]
    const landingLon = section.points?.[section.points.length - 1]?.[1]
    const next = starts.find((x) => x.at > takeOff)
    if (next !== undefined && next.at > landing && next.at - landing <= DAY_MS
      && !gleicheZone(zoneBei(clock, takeOff), next.e.tz_name) && fits(next.e.tz_name, landing, landingLon)) {
      return { entry: next.e, landing: section.to, early: false }
    }
    const before = [...starts].reverse().find((x) => x.at <= takeOff)
    if (before !== undefined && takeOff - before.at <= DAY_MS && (next === undefined || next.at > landing)
      && takeOffLon !== undefined && !fits(before.e.tz_name, takeOff, takeOffLon) && fits(before.e.tz_name, landing, landingLon)
      && !gleicheZone(zoneBei(clock, before.at - 1), before.e.tz_name)) {
      return { entry: before.e, landing: section.to, early: true }
    }
  }
  return null
}

/** "New York" out of `America/New_York` -- the city part, readable. */
export function stadtVon(name: string): string {
  const teil = name.split('/').pop() ?? name
  return teil.replace(/_/g, ' ')
}
